"use client";

import { GET_RBI_PRICE_INDEX_QUERY } from "@/gql/sup";
import { useQuery } from "@apollo/client";
import { useMemo } from "react";

/**
 * House Price Index (HPI) Quarter Data Type
 * Base Year: 2010-11 = 100
 * Source: RBI (Reserve Bank of India)
 */
export interface HPIQuarterData {
	quarter: string;
	value: number;
}

interface RBIPriceIndexResponse {
	seller: {
		getRBIPriceIndex: {
			data: {
				quarter: string;
				index: number;
			}[];
			latestQuarter: string;
			latestIndex: number;
		};
	};
}

/**
 * Hook to fetch House Price Index data from the backend GraphQL API
 * Data is fetched from the RBI Price Index endpoint
 *
 * @param skip - Optional flag to skip the query (useful for conditional fetching)
 */
export function useHPIData(skip = false) {
	const {
		data: queryData,
		loading,
		error,
	} = useQuery<RBIPriceIndexResponse>(GET_RBI_PRICE_INDEX_QUERY, {
		variables: {
			params: {
				getRBIPriceIndex: {
					_empty: null,
				},
			},
		},
		fetchPolicy: "cache-first", // Cache the data since it doesn't change frequently
		skip, // Skip query when not needed
	});

	// Transform the data to match the expected format
	const data = useMemo(() => {
		if (!queryData?.seller?.getRBIPriceIndex?.data) return null;

		return queryData.seller.getRBIPriceIndex.data.map(item => ({
			quarter: item.quarter,
			value: item.index,
		}));
	}, [queryData]);

	const latestQuarter = queryData?.seller?.getRBIPriceIndex?.latestQuarter;
	const latestIndex = queryData?.seller?.getRBIPriceIndex?.latestIndex;

	return { data, loading, error, latestQuarter, latestIndex };
}
